import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import type { ReadClientPlanItem } from "@ferrivia/api-contract";
import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { Button, Sheet, SheetContent, SheetTitle, Text } from "@ferrivia/ui";
import { useAppContext } from "../../../../app/useAppContext";
import { useTabNavigation } from "../../../../app/useTabNavigation";
import { useDismissOnBack } from "../../../../hooks/useDismissOnBack";
import { kindIcon, usePlanCopy } from "./usePlanCopy";
import s from "./PlanBlock.module.css";

export interface PlanItemSheetProps {
  // Null when closed. `shown` is the last opened item, kept through the close
  // animation so the copy can't blank out mid-slide (components/AnalyteInfoSheet).
  item: ReadClientPlanItem | null;
  shown: ReadClientPlanItem | null;
  onClose: () => void;
}

export function PlanItemSheet({ item, shown, onClose }: PlanItemSheetProps) {
  const { t } = useTranslation("client");
  const { rootElement } = useAppContext();
  const { goToTab } = useTabNavigation();
  const tracker = useTracker();
  const { signature, dateLine, channelText, durationLine } = usePlanCopy();

  const open = item !== null;
  useDismissOnBack(open, onClose, "plan-item");

  // Closing pops the sheet's own history entry (useDismissOnBack); switching
  // tab before that pop lands would have the pop undo the switch. So the tab
  // change waits until the sheet's entry is gone.
  const location = useLocation();
  const [toChat, setToChat] = useState(false);
  const onSheetEntry = ((location.state as { overlay?: string[] } | null)?.overlay ?? []).includes("plan-item");
  useEffect(() => {
    if (toChat && !open && !onSheetEntry) {
      setToChat(false);
      goToTab("/chat", "in_app_link");
    }
  }, [toChat, open, onSheetEntry, goToTab]);

  const askCareTeam = () => {
    tracker.track("home.block_cta_clicked", { block_id: "plan.chat" });
    setToChat(true);
    onClose();
  };

  const Icon = shown === null ? null : kindIcon(shown.kind);
  const date = shown === null ? null : dateLine(shown);
  const channel = shown === null ? null : channelText(shown);

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent container={rootElement} aria-describedby={undefined}>
        <div className={s.sheetHeader}>
          {Icon !== null && (
            <span className={s.tile}>
              <Icon aria-hidden size={20} />
            </span>
          )}
          <SheetTitle className={s.sheetTitle}>{shown?.title ?? ""}</SheetTitle>
        </div>
        {shown !== null && (
          <div className={s.sheetBody}>
            {shown.instructions !== null && (
              <Text as="p" size="sm" className={s.instructions}>
                {shown.instructions}
              </Text>
            )}
            <Text as="p" size="sm" className={s.detail}>
              {durationLine(shown)}
            </Text>
            {date !== null && (
              <Text as="p" size="sm" className={s.detail}>
                {channel === null ? date : `${date}, ${channel}`}
              </Text>
            )}
            <Text as="p" size="sm" className={s.signature}>
              {signature(shown)}
            </Text>
            <Button variant="secondary" className={s.ask} onClick={askCareTeam}>
              {t("home.blocks.plan.askCareTeam", "Kysyttävää? Kirjoita hoitotiimille")}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
